<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
      Account Security
    </h3>

    <!-- Google Account Link -->
    <div class="mb-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <svg width="24" height="24" viewBox="0 0 24 24" class="flex-shrink-0">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          <div>
            <p class="font-medium text-gray-900 dark:text-white">
              Google Account
            </p>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {{ googleLinked ? "Connected" : "Not connected" }}
            </p>
          </div>
        </div>
        <button
          v-if="!googleLinked"
          @click="linkGoogleAccount"
          :disabled="isLinking"
          class="px-4 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors disabled:opacity-50"
        >
          {{ isLinking ? "Linking..." : "Link Account" }}
        </button>
        <span
          v-else
          class="text-sm text-green-600 font-medium flex items-center gap-1"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
          Linked
        </span>
      </div>
    </div>

    <!-- 2FA Settings -->
    <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium text-gray-900 dark:text-white">
            Two-Factor Authentication
          </p>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Require phone verification when signing in with a linked provider
          </p>
        </div>
        <label class="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            :checked="twoFactorEnabled"
            @change="toggle2FA"
            class="sr-only peer"
            :disabled="isUpdating2FA"
          />
          <div
            class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"
          ></div>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";

export default {
  name: "AccountSecuritySettings",
  data() {
    return {
      googleLinked: false,
      twoFactorEnabled: false,
      isLinking: false,
      isUpdating2FA: false,
    };
  },
  computed: {
    authenticatedUser() {
      return this.$store.getters.getAuthenticatedUser;
    },
  },
  watch: {
    authenticatedUser: {
      immediate: true,
      handler(user) {
        if (user) {
          this.googleLinked = user.google_linked || false;
          this.twoFactorEnabled =
            user.two_factor_enabled !== undefined
              ? user.two_factor_enabled
              : true;
        }
      },
    },
  },
  methods: {
    async linkGoogleAccount() {
      this.isLinking = true;
      try {
        const { GoogleAuthProvider, signInWithPopup, getAuth } =
          await import("firebase/auth");
        const auth = getAuth();
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        const idToken = await result.user.getIdToken();

        await this.$store.dispatch("googleLinkAccount", {
          google_id_token: idToken,
        });

        this.googleLinked = true;
        toast.success("Google account linked successfully!");
      } catch (error) {
        const msg = error?.data?.message || "Failed to link Google account.";
        toast.error(msg);
      } finally {
        this.isLinking = false;
      }
    },
    async toggle2FA() {
      this.isUpdating2FA = true;
      const newValue = !this.twoFactorEnabled;
      try {
        await this.$store.dispatch("update2FASettings", {
          two_factor_enabled: newValue,
        });
        this.twoFactorEnabled = newValue;
        toast.success(newValue ? "2FA enabled" : "2FA disabled");
      } catch (error) {
        toast.error(
          error?.data?.message || "Failed to update 2FA settings.",
        );
      } finally {
        this.isUpdating2FA = false;
      }
    },
  },
  async mounted() {
    try {
      const settings = await this.$store.dispatch("get2FASettings");
      this.googleLinked = settings.google_linked || false;
      this.twoFactorEnabled = settings.two_factor_enabled || false;
    } catch (error) {
      void error;
    }
  },
};
</script>
